import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, inject } from '@angular/core';

import { SitecoreMobileConfirmationConfig } from '@frontend/sports/common/client-config-data-access';
import { UserService } from '@frontend/sports/user/feature';
import { DsButton } from '@frontend/ui/button';
import { ProductHomepagesConfig, WINDOW } from '@frontend/vanilla/core';
import { Observable, map } from 'rxjs';

import { LocalStorageService } from '../common/local-storage.service';
import { PopupManager } from '../popup/popup-manager.service';
import { MobileNumberAcknowledgement, UpdateMobileNumber } from './model/update-mobile-number.model';
import { FormatPhoneNumberPipe } from './pipes/format-phone-number.pipe';
import { TrackingValues, UpdateMobileNumberTrackingService } from './update-mobile-number-tracking.service';

@Component({
    selector: 'ms-update-mobile-number',
    standalone: true,
    templateUrl: './update-mobile-number.component.html',
    styleUrls: ['./update-mobile-number.component.scss'],
    imports: [FormatPhoneNumberPipe, DsButton, AsyncPipe],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [UpdateMobileNumberTrackingService],
})
export class UpdateMobileNumberComponent implements OnInit {
    mobileNumber: string | null;
    accountId: string;
    redirectURL: string;
    mobileNumberConfirmationConfig$: Observable<SitecoreMobileConfirmationConfig>;

    constructor(
        private userService: UserService,
        private localStorageService: LocalStorageService,
        private popupManager: PopupManager,
        private mobileNumberConfig: SitecoreMobileConfirmationConfig,
        private productHomepagesConfig: ProductHomepagesConfig,
        private updateMobileNumberTrackingService: UpdateMobileNumberTrackingService,
    ) {
        this.mobileNumberConfirmationConfig$ = this.mobileNumberConfig.whenReady.pipe(
            map(() => {
                this.redirectURL = this.mobileNumberConfig.updatePhoneNumberURL;
                return this.mobileNumberConfig;
            }),
        );
    }

    ngOnInit(): void {
        this.updateMobileNumberTrackingService.trackPopupShown();
        this.mobileNumber = this.userService.getUserMobileNumber();
        this.accountId = this.userService.accountId ?? '';
    }

    confirmMobileNumber(): void {
        this.updateMobileNumberTrackingService.trackButtonClick({ details: TrackingValues.Confirm });
        this.closePopup();
        this.saveUserAction();
    }

    updateMobileNumber(): void {
        const url = `${this.productHomepagesConfig.portal}${this.redirectURL}`;
        this.saveUserAction();
        this.updateMobileNumberTrackingService.trackButtonClick({ details: TrackingValues.UpdateMobileNumber, url: url });
        this.#window.location.href = url;
    }

    private closePopup(): void {
        if (this.popupManager.isPopupOpen(UpdateMobileNumber.PopupIdentifier)) {
            this.popupManager.close();
        }
    }

    private saveUserAction(): void {
        const savedAcknowledgementList: MobileNumberAcknowledgement | null = this.localStorageService.getItemObject(
            UpdateMobileNumber.StorageItemIdentifier,
        );
        const acknowledgementList: MobileNumberAcknowledgement = savedAcknowledgementList ?? {};
        acknowledgementList[this.accountId] = new Date().getTime();
        this.localStorageService.saveItem<MobileNumberAcknowledgement>(UpdateMobileNumber.StorageItemIdentifier, acknowledgementList);
    }

    readonly #window = inject(WINDOW);
}
